import React from 'react';
import { navigate } from 'gatsby';
import { useCustomerContext, isExpired } from '../../contexts/CustomerContext';
import { NanoGlobalNav } from '@nanoporetech-digital/components-react';
import MegaDropDown from '../MegaDropDown';
import Footer from "../Footer";
import * as styles from "./style.module.css";
import cx from 'classnames';
import PersonaSwitcher from "./PersonaSwitcher/personaSwitcher";
import { useCartContext } from '../../contexts/CartContext';
import useLoginLink, { getReAuthenticateUrl, getCartUrl } from '../../hooks/useLoginLink';
import { isProduction } from '../../hooks/useEnvironment';
import useSiteData, { isLoginRequired } from '../../hooks/siteData';
import Mavenoid, { loadMavenoid } from './mavenoid';
import { identifyContact } from '../../utils/hubspot';
import LoggedInIndicator from './LoggedInIndicator/loggedInIndicator';
import { heapIdentify } from '../../utils/heap';

const MY_ACCOUNT_URL = isProduction()
    ? 'https://myaccount.nanoporetech.com'
    : 'https://myaccount-test.nanoporetech.com';

export default function GlobalNav({children}) {
    const { customer, resetCustomer } = useCustomerContext();
    const {cart} = useCartContext();
    const cartCount = cart?.items?.length ?? 0;
    const { home } = useSiteData();
    const reAuthLink = getReAuthenticateUrl();
    const loginLink = useLoginLink();
    const cartLink = getCartUrl();

    function handleUserData(e) {
        if (typeof window !== 'undefined') {
            const user = e.target.myAccountUser?.uuid ? e.target.myAccountUser : null;
            forceLoginIfNeeded(customer, user, loginLink, resetCustomer);
            heapIdentify(user);
            identifyContact(user?.email);
            loadMavenoid(user);
            updateCartCookie(cartCount);
        }
    }

    return(
        <NanoGlobalNav
        id="global-nav-main"
        className={customer.token ? 'loggedIn' : ''}
        logoUrl={home}
        myAccountUrl={MY_ACCOUNT_URL}
        searchIndex="Store"
        cartUrl={cartLink}
        cartCount={cartCount}
        sessionRedirect={reAuthLink}
        onNanoUserData={e => handleUserData(e)}>
            <Mavenoid />
            <MegaDropDown />
            <LoggedInIndicator />
            <PersonaSwitcher />
            <div className={styles.siteBody}>
                <main className={cx("site-main", styles.siteMain)}>
                    <div className={cx("site-main__content", styles.siteMain__content)}>{children}</div>
                </main>
                <Footer />
            </div>
        </NanoGlobalNav>
    );
}

function forceLoginIfNeeded(contextCustomer, user, loginLink, resetCustomer) {
    const ssoUser = contextCustomer.sso_user;
    const userHasChanged = user && ssoUser && ssoUser != user.id;
    const sessionExpired = isExpired(contextCustomer);

    if (contextCustomer.is_admin_mode || contextCustomer.is_remote_mode) {
        if (sessionExpired) resetCustomer();
        return;
    }

    if (!user && !isLoginRequired()) {
        return;
    }

    // it is a standard session and we have a user or we don't have a user but login is REQUIRED

    if (user && !contextCustomer.token && contextCustomer.expiry && !sessionExpired) {
        // prevent /reauthenticate loop
        return;
    }

    if (!contextCustomer.token || userHasChanged || sessionExpired) {
        const url = new URL(window.location.href);
        if (!url.pathname.toLowerCase().includes('authenticate')) {
            console.log('Mandatory login');
            navigate(loginLink);
        }
    }
}

function updateCartCookie(cartCount) {
    if (typeof window === 'undefined') {
        return;
    }

    const name = isProduction() ? 'ONTCART' : 'ONTCART-TEST';
    const domain = ".nanoporetech.com";
    const maxAge = 30 * 60; // 30 minutes
    const cartUrl = window.location.origin + '/cart/';

    const cookieData = [
        cartCount,
        cartUrl
    ];

    const encodedValue = btoa(
        JSON.stringify(cookieData)
    );

    document.cookie = name + '=' + encodedValue
        + ';Domain=' + domain
        + ';Max-Age=' + maxAge;
}
